/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Content Helper utilities for MathJax and Markdown processing
 *
 * @file markDowLoader.js
 * @author Murhsid Ahamedh
 */


const setupMarkedExtensions = () => {
  // Custom tokenizer for inline math delimiters: \( ... \) and \[ ... \]
  const mathTokenizer = {
    name: 'MATH_EXPRESSION',
    level: 'inline',
    // eslint-disable-next-line consistent-return
    tokenizer(src) {
      // Match inline math with \( ... \)
      const inlineMathMatch = /^\\\((.*?)\\\)/.exec(src);
      if (inlineMathMatch) {
        return {
          type: 'MATH_EXPRESSION',
          subType: 'LATEX_BRACKET',
          raw: inlineMathMatch[0],
          text: inlineMathMatch[1],
          tokens: []
        };
      }

      // Match inline math with \[ ... \] (treat as inline math)
      const blockMathMatch = /^\\\[(.*?)\\\]/s.exec(src); // Added "s" for dot-all mode to match multi-line
      if (blockMathMatch) {
        return {
          type: 'MATH_EXPRESSION',
          subType: 'LATEX_SQ_BRACKET',
          raw: blockMathMatch[0],
          text: blockMathMatch[1],
          tokens: []
        };
      }
    },

    // eslint-disable-next-line consistent-return
    renderer(token) {
      // Render both inline and block delimiters as inline math
      if (token.type === 'MATH_EXPRESSION' && token.subType === 'LATEX_BRACKET') {
        return `\\(${token.text}\\)`; // Render inline math
      }
      if (token.type === 'MATH_EXPRESSION' && token.subType === 'LATEX_SQ_BRACKET') {
        return `\\[${token.text}\\]`; // Render inline math
      }
    }
  };
  // custom math extension to Marked.js

  window.marked.use({ extensions: [mathTokenizer] });
};

// eslint-disable-next-line import/prefer-default-export
export const loadMarkdownUrl = () => new Promise((resolve, reject) => {
  // Check if marked is already available
  if (window.marked && window.marked.parse) {
    setupMarkedExtensions();
    resolve();

    return;
  }

  // Check if script is already loading
  if (document.querySelector('script[src*="marked"]')) {
    // Wait for existing script to load
    const checkInterval = setInterval(() => {
      if (window.marked && window.marked.parse) {
        clearInterval(checkInterval);
        setupMarkedExtensions();
        resolve();
      }
    }, 100);

    // Timeout after 10 seconds
    setTimeout(() => {
      clearInterval(checkInterval);
      if (!window.marked || !window.marked.parse) {
        reject(new Error('Marked loading timeout'));
      }
    }, 10000);

    return;
  }

  const scriptElement = document.createElement('script');
  scriptElement.type = 'text/javascript';
  scriptElement.src = 'https://cdn.jsdelivr.net/npm/marked/marked.min.js';
  scriptElement.async = true;

  scriptElement.onload = () => {
    // Verify marked loaded correctly
    if (window.marked && window.marked.parse) {
      setupMarkedExtensions();
      resolve();
    } else {
      reject(new Error('Marked loaded but parse method not available'));
    }
  };

  scriptElement.onerror = () => {
    reject(new Error('Failed to load Marked from CDN'));
  };

  document.body.appendChild(scriptElement);
});
